import React, { useState, useEffect } from 'react';

const WHATSAPP_NUMBER = '5926511955';

const WhatsAppButton = () => {
  const [showAvatar, setShowAvatar] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  // Alternate between avatar and WhatsApp icon every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShowAvatar(prev => !prev);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const handleClick = () => {
    const currentUrl = window.location.href;
    const message = `Hi, I would like to order this item: ${currentUrl}`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
      {/* Speech Bubble - shows when avatar is visible */}
      <div 
        className={`
          transition-all duration-500 ease-in-out mb-3
          ${showAvatar || isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
        `}
      >
        <div className="relative bg-white rounded-2xl shadow-xl px-4 py-3 max-w-[200px]">
          <p className="text-sm text-gray-800 font-medium leading-snug">
            {showAvatar ? (
              <>👋 Click me to connect and place your order!</>
            ) : (
              <>💬 Chat with us on WhatsApp!</>
            )}
          </p>
          {/* Speech bubble arrow */}
          <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white transform rotate-45 shadow-lg"></div>
        </div>
      </div>

      {/* Button Container */}
      <button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="relative w-16 h-16 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 overflow-hidden"
        aria-label="Order via WhatsApp"
      >
        {/* Avatar View */}
        <div 
          className={`
            absolute inset-0 transition-all duration-500 ease-in-out
            ${showAvatar ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
          `}
        >
          {/* 3D Animated Avatar Container */}
          <div className="w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-full flex items-center justify-center">
            {/* Animated 3D Character */}
            <div className="avatar-container">
              {/* Head */}
              <div className="avatar-head">
                {/* Face */}
                <div className="avatar-face">
                  {/* Eyes */}
                  <div className="avatar-eyes">
                    <div className="avatar-eye left">
                      <div className="avatar-pupil"></div>
                    </div>
                    <div className="avatar-eye right">
                      <div className="avatar-pupil"></div>
                    </div>
                  </div>
                  {/* Mouth - Animated talking */}
                  <div className="avatar-mouth"></div>
                </div>
              </div>
              {/* Body hint */}
              <div className="avatar-body"></div>
              {/* Waving hand */}
              <div className="avatar-hand">👋</div>
            </div>
          </div>
          
          {/* Animated ring */}
          <div className="absolute inset-0 rounded-full border-4 border-white/30 animate-ping"></div>
        </div>

        {/* WhatsApp Icon View */}
        <div 
          className={`
            absolute inset-0 bg-[#25D366] rounded-full flex items-center justify-center
            transition-all duration-500 ease-in-out
            ${!showAvatar ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}
          `}
        >
          <svg
            viewBox="0 0 24 24"
            className="w-8 h-8 fill-white"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          {/* Pulse effect */}
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-25"></span>
        </div>

        {/* WhatsApp badge when avatar is showing */}
        {showAvatar && (
          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg border-2 border-white">
            <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
        )}
      </button>

      {/* CSS Styles for 3D Avatar */}
      <style>{`
        .avatar-container {
          position: relative;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .avatar-head {
          width: 32px;
          height: 32px;
          background: linear-gradient(145deg, #FFE0BD, #FFCBA4);
          border-radius: 50%;
          position: relative;
          box-shadow: 
            inset -3px -3px 6px rgba(0,0,0,0.1),
            inset 3px 3px 6px rgba(255,255,255,0.4),
            0 4px 8px rgba(0,0,0,0.2);
          animation: headBob 2s ease-in-out infinite;
        }
        
        @keyframes headBob {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-2px) rotate(2deg); }
        }
        
        .avatar-face {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100%;
          height: 100%;
        }
        
        .avatar-eyes {
          position: absolute;
          top: 35%;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
        }
        
        .avatar-eye {
          width: 6px;
          height: 6px;
          background: #2D1B0E;
          border-radius: 50%;
          position: relative;
          animation: blink 3s ease-in-out infinite;
        }
        
        @keyframes blink {
          0%, 90%, 100% { transform: scaleY(1); }
          95% { transform: scaleY(0.1); }
        }
        
        .avatar-pupil {
          width: 2px;
          height: 2px;
          background: white;
          border-radius: 50%;
          position: absolute;
          top: 1px;
          left: 1px;
        }
        
        .avatar-mouth {
          position: absolute;
          bottom: 25%;
          left: 50%;
          transform: translateX(-50%);
          width: 10px;
          height: 5px;
          background: #C9544D;
          border-radius: 0 0 10px 10px;
          animation: talk 0.3s ease-in-out infinite;
        }
        
        @keyframes talk {
          0%, 100% { 
            height: 5px; 
            border-radius: 0 0 10px 10px;
          }
          50% { 
            height: 8px; 
            border-radius: 50%;
          }
        }
        
        .avatar-body {
          position: absolute;
          bottom: -4px;
          width: 24px;
          height: 12px;
          background: linear-gradient(145deg, #4F46E5, #7C3AED);
          border-radius: 12px 12px 0 0;
          box-shadow: 
            inset -2px -2px 4px rgba(0,0,0,0.2),
            inset 2px 2px 4px rgba(255,255,255,0.2);
        }
        
        .avatar-hand {
          position: absolute;
          right: -8px;
          top: 8px;
          font-size: 14px;
          animation: wave 1s ease-in-out infinite;
          transform-origin: bottom center;
        }
        
        @keyframes wave {
          0%, 100% { transform: rotate(-10deg); }
          50% { transform: rotate(20deg); }
        }
      `}</style>
    </div>
  );
};

export default WhatsAppButton;
